import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type {
  ReadPostgridCheque,
  ReadPostgridLetter,
} from '@/services/api/postgridService';
import {
  usePostgridChequesCancelMutation,
  usePostgridChequesProgressTestMutation,
  usePostgridLettersCancelMutation,
  usePostgridLettersProgressTestMutation,
} from '@/services/api/postgridService';

import {
  CancelButton,
  ProgressTestButton,
  StatusIndicatorWithActions,
  TestModeCardContainer,
} from '../../common';

export interface MailableListCardProps {
  mailable: ReadPostgridCheque | ReadPostgridLetter;
  onDetailsClick?: (id: string) => void;
}

// Type guard to check if the mailable is a cheque
const isCheque = (
  mailable: ReadPostgridCheque | ReadPostgridLetter,
): mailable is ReadPostgridCheque => {
  return 'amount' in mailable;
};

export const MailableListCard: React.FC<MailableListCardProps> = ({
  mailable,
  onDetailsClick,
}) => {
  const [cancelCheque] = usePostgridChequesCancelMutation();
  const [progressTestCheque] = usePostgridChequesProgressTestMutation();
  const [cancelLetter] = usePostgridLettersCancelMutation();
  const [progressTestLetter] = usePostgridLettersProgressTestMutation();

  const isTest = mailable.is_test ?? !mailable.live;
  const type = isCheque(mailable) ? 'Cheque' : 'Letter';
  const canCancel =
    mailable.status === 'creating' || mailable.status === 'ready';
  const showCancelOption =
    mailable.status !== 'cancelled' && mailable.status !== 'delivered' && mailable.status !== 'failed';

  const getCancelDisabledReason = () => {
    const statusLabels: Record<string, string> = {
      printing: 'printing',
      processed_for_delivery: 'processed for delivery',
      completed: 'sent to carrier',
      in_transit: 'in transit',
      in_local_area: 'in local area',
      out_for_delivery: 'out for delivery',
      re_routed: 're-routed',
      returned_to_sender: 'returned to sender',
      processed: 'processed',
    };
    const label = statusLabels[mailable.status || ''] || mailable.status || 'unknown';
    return `Cannot cancel \u2014 ${type.toLowerCase()} is already ${label}`;
  };

  const handleCancel = async () => {
    try {
      if (isCheque(mailable)) {
        await cancelCheque({ id: mailable.id }).unwrap();
        toast.success('Cheque cancelled successfully');
      } else {
        await cancelLetter({ id: mailable.id }).unwrap();
        toast.success('Letter cancelled successfully');
      }
    } catch (error) {
      const errorMessage =
        (error as { data?: { error?: string } })?.data?.error ||
        `Failed to cancel ${type.toLowerCase()}`;
      toast.error(errorMessage);
      console.error('Cancel error:', error);
    }
  };

  const handleProgressTest = async () => {
    try {
      if (isCheque(mailable)) {
        await progressTestCheque({ id: mailable.id }).unwrap();
        toast.success('Test cheque progressed successfully');
      } else {
        await progressTestLetter({ id: mailable.id }).unwrap();
        toast.success('Test letter progressed successfully');
      }
    } catch (error) {
      toast.error(`Failed to progress test ${type.toLowerCase()}`);
      console.error('Progress test error:', error);
    }
  };

  const handleDetailsClick = () => {
    onDetailsClick?.(mailable.id);
  };

  const renderMailableDetails = () => {
    if (isCheque(mailable)) {
      return (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-sm text-gray-900">
              ${parseFloat(mailable.amount).toFixed(2)}
            </h3>
            <StatusIndicatorWithActions status={mailable.status} />
          </div>
          {mailable.memo && (
            <p className="text-xs text-gray-600 line-clamp-2">
              {mailable.memo}
            </p>
          )}
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">
              {type}
            </Badge>
            <span className="text-xs text-gray-500">
              {mailable.cheque_type}
            </span>
          </div>
        </div>
      );
    }
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-sm text-gray-900">
            {mailable.subject || 'Untitled Letter'}
          </h3>
          <StatusIndicatorWithActions
            status={mailable.status}
            layout="stacked"
          />
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">
            {type}
          </Badge>
          {mailable.color && (
            <Badge variant="outline" className="text-xs">
              Color
            </Badge>
          )}
          {mailable.double_sided && (
            <Badge variant="outline" className="text-xs">
              Double-sided
            </Badge>
          )}
        </div>
      </div>
    );
  };

  const cardContent = (
    <Card className="cursor-pointer hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div
            className="flex-1 min-w-0"
            onClick={handleDetailsClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleDetailsClick();
              }
            }}
          >
            {renderMailableDetails()}
            <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
              <span>ID: {mailable.postgrid_id}</span>
              <span>
                {mailable.created_at
                  ? new Date(mailable.created_at).toLocaleDateString()
                  : 'N/A'}
              </span>
            </div>
          </div>

          <Menu as="div" className="relative ml-2">
            <MenuButton
              as={Button}
              variant="ghost"
              size="sm"
              className="p-1 h-8 w-8"
            >
              <EllipsisVerticalIcon className="h-4 w-4" />
            </MenuButton>
            <MenuItems className="absolute right-0 z-10 mt-1 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <MenuItem>
                <button
                  className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                  onClick={handleDetailsClick}
                  type="button"
                >
                  View Details
                </button>
              </MenuItem>

              {/* Only show Progress Test for test mode items */}
              {isTest && (
                <MenuItem>
                  <ProgressTestButton
                    isTest={isTest}
                    onProgress={handleProgressTest}
                  />
                </MenuItem>
              )}

              {showCancelOption && (
                <MenuItem disabled={!canCancel}>
                  {canCancel ? (
                    <CancelButton
                      onConfirm={handleCancel}
                      entityType={isCheque(mailable) ? 'cheque' : 'letter'}
                    />
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="block w-full px-4 py-2 text-left text-sm text-gray-400 cursor-not-allowed">
                          Cancel {type.toLowerCase()}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        {getCancelDisabledReason()}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </MenuItem>
              )}
            </MenuItems>
          </Menu>
        </div>
      </CardContent>
    </Card>
  );

  // Wrap in TestModeCardContainer if it's a test item
  if (isTest) {
    return (
      <TestModeCardContainer isTest={isTest}>
        {cardContent}
      </TestModeCardContainer>
    );
  }

  return cardContent;
};
